/**
 * Yürürlükteki yasal metin sürümleri. Metin değişince sürüm artar ve
 * kullanıcıdan yeniden rıza istenir. Rıza kayıtları DENETLENEBİLİRDİR
 * (kırmızı çizgi 5); hangi sürüme rıza verildiği saklanır.
 *
 * SÜRÜM BİR ISO TARİHTİR (`YYYY-AA-GG`). Karşılaştırma sözlükseldir ve bu
 * biçimde sözlüksel sıra takvim sırasıyla aynıdır; ay ve yıl geçişinde de
 * (`consent-policy.guard.spec.ts` ölçer). Biçim değişirse o kapı kırılır.
 *
 * 2026-09-17 (öneri motoru Blok 1, Story 8): gizlilik politikası, aydınlatma
 * metni ve analitik rızası yeni veri kategorisini (gösterilen öneriler ve
 * onlara ne olduğu) anlatacak biçimde güncellendi; üç sürüm birlikte arttı.
 * Kullanım koşulları, pazarlama ve katılımcı görünürlüğü metinleri DEĞİŞMEDİ,
 * sürümleri yerinde kaldı: değişmeyen metne yeniden rıza istemek rızayı
 * anlamsızlaştırır.
 *
 * 2026-10-09 (katılımcı listesi kaydı izler, kırmızı çizgi 2): kullanım
 * koşulları, gizlilik politikası, KVKK aydınlatma metni ve açık rıza metni yeni
 * dayanağa (sözleşmenin ifası) göre TASLAK olarak yeniden yazıldı. Sürümler o
 * gün avukat onayı beklendiği için ilerletilmedi; 14.10, 16.10 ve 20.10
 * değişiklikleri de aynı gerekçeyle sürümsüz kaldı.
 *
 * 2026-11-02 (kullanıcı kararı 02.11.2026, `docs/riza-surumu-kapsam-2026-11-02.md`
 * Story 2): avukat onayı beklenmeden, metni sürüm gününden SONRA değişen dört
 * tür ilerletildi: kullanım koşulları, gizlilik politikası, KVKK aydınlatma
 * metni ve analitik rızası (açık rıza metninin `analitik` bölümü). Pazarlama
 * rızasının bölümü ve etiketleri değişmedi (yalnız açık rıza metninin ortak
 * girişi değişti), sürümü yerinde kaldı. Kurum hesabı aydınlatma metnine bu
 * kararda dokunulmadı. Eski sürümler `CONSENT_PREVIOUS_DOCUMENT_VERSIONS`e
 * geçiş süresiyle yazıldı. Hangi metnin değiştiği depo geçmişinden ölçüldü.
 * Metin ile sürüm arasındaki bağı `legal/consent-text-seal.guard.spec.ts`
 * ölçer: metin değişip sürüm ilerlemezse kırılır.
 * TODO(tech-debt): metinler TASLAKTIR (avukat onayı, paket S4). Onaylı metin
 * içeriği değiştirirse sürüm aynı akışla yeniden ilerler.
 */
export const CONSENT_DOCUMENT_VERSIONS = {
    terms_of_service: '2026-11-02',
    privacy_policy: '2026-11-02',
    kvkk_clarification: '2026-11-02',
    marketing_email: '2026-09-08',
    analytics: '2026-11-02',
    attendee_visibility: '2026-09-08',
    /*
     * 2026-10-03 (kurum hesabı · Story H5): kurum hesabı aydınlatma metni.
     * TODO(tech-debt): metin TASLAKTIR, avukat onayı canlıya çıkış kriteridir;
     * onaylı metin gelince sürüm ilerler.
     */
    organization_account_notice: '2026-10-03',
};
/**
 * Verilen sürüm YÜRÜRLÜKTEKİ sürüm mü. Bir rıza ancak üç şart birden
 * sağlanınca "aktif"tir: verildi · geri çekilmedi · YÜRÜRLÜKTEKİ metne verildi.
 * Üçüncü şart tek yerden sorulur; sunucu (`hasActiveConsent`) ve istemci
 * (anahtarın "açık" görünümü) aynı cevabı alır. Eski metne verilmiş rıza
 * defterde durur (denetlenebilirlik, kırmızı çizgi 5) ama yeni metnin
 * anlattığı işlemeye izin VERMEZ (Story 8, 17.09.2026).
 */
export function isCurrentConsentVersion(type, documentVersion) {
    return documentVersion === CONSENT_DOCUMENT_VERSIONS[type];
}
export const CONSENT_PREVIOUS_DOCUMENT_VERSIONS = {
    terms_of_service: { version: '2026-09-08', acceptUntil: '2026-11-12' },
    privacy_policy: { version: '2026-09-17', acceptUntil: '2026-11-12' },
    kvkk_clarification: { version: '2026-09-17', acceptUntil: '2026-11-12' },
    analytics: { version: '2026-09-17', acceptUntil: '2026-11-12' },
};
/**
 * Yazma yolunda (kayıt, `POST /consents`) kabul edilen sürüm mü: yürürlükteki
 * sürüm ya da geçiş süresi dolmamış bir önceki sürüm.
 */
export function isAcceptableConsentVersion(type, documentVersion, now, previous = CONSENT_PREVIOUS_DOCUMENT_VERSIONS) {
    if (isCurrentConsentVersion(type, documentVersion))
        return true;
    const grace = previous[type];
    if (!grace || documentVersion !== grace.version)
        return false;
    // Bitiş günü UTC gece yarısında başlar; gün parçalardan kurulur (takvim gününe saat eklenmez).
    const [year = 0, month = 1, day = 1] = grace.acceptUntil.split('-').map(Number);
    return now.getTime() < Date.UTC(year, month - 1, day);
}
/** Yazma yolunda kabul edilen sürümler (hata yanıtının `valids` parametresi için). */
export function acceptableConsentVersions(type, now, previous = CONSENT_PREVIOUS_DOCUMENT_VERSIONS) {
    const grace = previous[type];
    const current = CONSENT_DOCUMENT_VERSIONS[type];
    return grace && isAcceptableConsentVersion(type, grace.version, now, previous)
        ? [current, grace.version]
        : [current];
}
/**
 * Hesap açılışında zorunlu olan rızalar. Zorunlu hizmet, pazarlama rızasına
 * BAĞLANAMAZ (bağlanırsa rıza geçerli sayılmaz) — pazarlama/analitik ayrı ve
 * varsayılan işaretsizdir.
 */
export const REQUIRED_CONSENT_TYPES = [
    'terms_of_service',
    'privacy_policy',
    'kvkk_clarification',
];
/** Zorunlu olmayan, kayıt ekranında AYRI ve İŞARETSİZ sunulan rızalar. */
export const OPTIONAL_SIGNUP_CONSENT_TYPES = [
    'marketing_email',
    'analytics',
];
/**
 * KURUM HESABININ RIZA SETİ (kullanıcı kararı 02.10.2026 · V11).
 *
 * Kullanım koşulları + kurum hesabı aydınlatma metni. Görünürlük, pazarlama ve
 * analitik rızası SORULMAZ: kurum hesabında ilgili işleme yoktur (katılımcı
 * listesi, etkinlik duyurusu, öneri kaydı). Kişisel hesabın gizlilik politikası
 * ve KVKK aydınlatma metni kurum hesabının işlemesini anlatmaz; onların yerini
 * kurum hesabı aydınlatma metni alır.
 */
export const ORGANIZATION_ACCOUNT_REQUIRED_CONSENT_TYPES = [
    'terms_of_service',
    'organization_account_notice',
];
/** Yalnız kurum hesabının verebildiği türler; kişisel hesap bunları VEREMEZ. */
export const ORGANIZATION_ONLY_CONSENT_TYPES = [
    'organization_account_notice',
];
/**
 * EMEKLİ RIZA TÜRLERİ (kullanıcı kararı 21.09.2026, kırmızı çizgi 2).
 *
 * `attendee_visibility` eski isteğe bağlı listenin rızasıydı. Yeni modelde
 * kaydolan listede görünür ve dayanak sözleşmenin ifasıdır, bu yüzden tür artık
 * VERİLEMEZ: hiçbir grupta sunulmaz, kayıt ekranında sorulmaz, `POST /consents`
 * vermeyi reddeder. Tür enum'da ve veritabanında KALIR, çünkü defterdeki eski
 * satırlar SİLİNMEZ (kırmızı çizgi 5, denetlenebilirlik). Var olan satır
 * hiçbir işlemeye izin vermez (`hasActiveConsent` her zaman `false`) ama geri
 * çekilebilir: rızayı geri alma hakkı kısıtlanamaz.
 */
export const RETIRED_CONSENT_TYPES = ['attendee_visibility'];
export function isRetiredConsentType(type) {
    return RETIRED_CONSENT_TYPES.includes(type);
}
/** Hesap açılışında ZORUNLU rıza türleri, hesap türüne göre. */
export function requiredConsentTypesFor(kind) {
    return kind === 'organization'
        ? ORGANIZATION_ACCOUNT_REQUIRED_CONSENT_TYPES
        : REQUIRED_CONSENT_TYPES;
}
/**
 * Hesabın VEREBİLECEĞİ rıza türleri. Kurum hesabı yalnız kendi setini verir.
 * Kişisel hesap kurum hesabına özgü metin dışındaki her türü verebilir.
 * Emekli tür (`RETIRED_CONSENT_TYPES`) HİÇBİR hesapta verilemez.
 */
export function isConsentTypeApplicable(kind, type) {
    if (isRetiredConsentType(type))
        return false;
    return kind === 'organization'
        ? ORGANIZATION_ACCOUNT_REQUIRED_CONSENT_TYPES.includes(type)
        : !ORGANIZATION_ONLY_CONSENT_TYPES.includes(type);
}
/**
 * Hesap varken GERİ ÇEKİLEMEYEN türler: iki türün zorunlu setlerinin birleşimi.
 * Bir türün zorunlu olduğu hesapta geri çekilmesi hizmeti imkânsız kılar, yolu
 * hesap silmedir. Kurum hesabı kişisel setin türlerini zaten veremez, kişisel
 * hesap da kurum metnini veremez; birleşim iki hesapta da doğru cevabı verir.
 */
export const NON_REVOCABLE_CONSENT_TYPES = [
    ...new Set([...REQUIRED_CONSENT_TYPES, ...ORGANIZATION_ACCOUNT_REQUIRED_CONSENT_TYPES]),
];
//# sourceMappingURL=consent-policy.js.map